import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { Download, FileText, RefreshCcw, Search, Users } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest } from "../../../redux/actions/payrollActions";
import { getRequest, API_BASE_URL } from "../../../../api";
import "./PaieCyclePages.css";

const userName = (user) => user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || `#${user?.id}`;

const money = (value) => {
    const n = Number(value || 0);
    const fixed = n.toFixed(2);
    const [intPart, decPart] = fixed.split(".");
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${intFormatted},${decPart}`;
};

const amount = (value) => Number(value || 0);
const monthLabel = (periode) => periode ? `${String(periode.mois).padStart(2, "0")}/${periode.annee}` : "-";
const safeFile = (value) => String(value || "document").replace(/[^\w-]+/g, "_");

const PRIMARY = [32, 67, 133];    // #204385
const ACCENT  = [238, 100, 95];   // #ee645f

const numberToWordsFR = (value) => {
    const units = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf"];
    const teens = ["dix", "onze", "douze", "treize", "quatorze", "quinze", "seize", "dix-sept", "dix-huit", "dix-neuf"];
    const tens = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante-dix", "quatre-vingt", "quatre-vingt-dix"];
    const parseUnderThousand = (num) => {
        let text = "";
        const hundred = Math.floor(num / 100);
        const rest = num % 100;
        if (hundred) text += `${hundred === 1 ? "" : `${units[hundred]} `}cent${hundred > 1 && rest === 0 ? "s" : ""} `;
        if (rest < 10) text += units[rest];
        else if (rest < 20) text += teens[rest - 10];
        else {
            const ten = Math.floor(rest / 10);
            const unit = rest % 10;
            text += tens[ten] + (unit === 1 ? "-et-un" : unit > 1 ? `-${units[unit]}` : "");
        }
        return text.trim();
    };
    let n = Math.floor(amount(value));
    if (!n) return "zero Dirhams";
    let text = "";
    if (n >= 1000) {
        const thousand = Math.floor(n / 1000);
        text += `${thousand === 1 ? "" : `${parseUnderThousand(thousand)} `}mille `;
        n %= 1000;
    }
    if (n) text += parseUnderThousand(n);
    return `${text.trim()} Dirhams`;
};

const addCompanyHeader = (doc, title, periodLabel) => {
    doc.setFillColor(...PRIMARY);
    doc.rect(0, 0, 210, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(title, 105, 7, { align: "center" });
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Raison sociale :", 118, 20);
    doc.setFont("helvetica", "normal");
    doc.text("BLUE SCRATCH CONSULTING", 145, 20);
    doc.setFont("helvetica", "bold");
    doc.text("Affiliation CNSS :", 118, 27);
    doc.setFont("helvetica", "normal");
    doc.text("2032565", 145, 27);
    doc.setFont("helvetica", "bold");
    doc.text("Adresse :", 118, 34);
    doc.setFont("helvetica", "normal");
    doc.text("Casablanca, Maroc", 145, 34);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`Période : ${periodLabel}`, 12, 20);
};

const addPayslipPage = (doc, item, periodLabel) => {
    const { bulletin: b, user: u, lines } = item;
    addCompanyHeader(doc, "BULLETIN DE PAIE", periodLabel);

    autoTable(doc, {
        startY: 40,
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.4, lineColor: 180, lineWidth: 0.1 },
        headStyles: { fillColor: PRIMARY, textColor: 255, halign: "center" },
        head: [["Matricule", "Nom et Prenom", "N° CNSS", "CIN", "Date de naissance", "Déduction", "Date d'embauche"]],
        body: [[u?.employeeCode || "-", userName(u), u?.numCnss || "-", u?.cin || "-", u?.dateOfBirth || "-", "0 / 0", u?.hireDate || "-"]],
    });

    autoTable(doc, {
        startY: doc.lastAutoTable.finalY,
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.2, lineColor: 180, lineWidth: 0.1 },
        headStyles: { fillColor: PRIMARY, textColor: 255, halign: "center" },
        head: [["Adresse", "Fonction", "CIMR"]],
        body: [[u?.adresse || "-", u?.jobName || "-", u?.numRetraite || "-"]],
    });

    const salaryRows = lines.map((line) => {
        const isPatronal = line.type === "SOUS_TOTAL" && String(line.rubriqueCode || "").startsWith("COT_PAT_");
        const isRetenue  = line.type === "RETENUE";
        const isGain     = line.type === "GAIN";
        const isBaseSalary = line.rubriqueCode === "SALAIRE_BASE";
        return [
            line.libelle || line.rubriqueCode || "-",                                              // Libellé
            line.baseCalcul != null ? money(line.baseCalcul) : "",                                 // Base en DH
            isBaseSalary ? money(b.nombreJoursTravailles) : line.tauxApplique != null ? money(amount(line.tauxApplique) * 100) : "",  // Taux
            isBaseSalary ? "J" : line.tauxApplique != null ? "%" : "",                            // unité
            isGain     ? money(line.montant) : "",                                                 // Gain
            isRetenue  ? money(line.montant) : "",                                                 // Retenue
            isPatronal ? money(line.montant) : "",                                                 // P. patronale
        ];
    });

// Lignes spéciales — toujours 7 colonnes
    salaryRows.splice(1, 0, ["Salaire Brut imposable",  money(b.salaireBrutImposable), "", "", "", "", ""]);
    salaryRows.push(       ["Salaire brut global",       money(b.salaireBrutGlobal),   "", "", "", "", ""]);
    salaryRows.push(       ["Gain Net",                  money(b.gainNet),             "", "", "", "", ""]);
    salaryRows.push(       ["Arrondis",                  "",                           "", "", money(b.arrondi), "", ""]);

    const totalGains = lines.filter((line) => line.type === "GAIN").reduce((sum, line) => sum + amount(line.montant), 0);
    const totalRetenues = lines.filter((line) => line.type === "RETENUE").reduce((sum, line) => sum + amount(line.montant), 0);
    const totalPatronales = lines
        .filter((line) => line.type === "SOUS_TOTAL" && String(line.rubriqueCode || "").startsWith("COT_PAT_"))
        .reduce((sum, line) => sum + amount(line.montant), 0);

    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 3,
        margin: { left: 12, right: 12 },
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.4, lineColor: 180, lineWidth: 0.12 },
        headStyles: { fillColor: PRIMARY, textColor: 255, halign: "center" },
        columnStyles: {
            0: { cellWidth: 56 },                    // Libelle : 62 → 56
            1: { cellWidth: 24, halign: "right" },   // Base en DH
            2: { cellWidth: 18, halign: "right" },   // Taux/nombre
            3: { cellWidth: 14, halign: "center" },  // %/J  : 8 → 14
            4: { cellWidth: 30, halign: "right" },   // Gain
            5: { cellWidth: 24, halign: "right" },   // Retenue
            6: { cellWidth: 24, halign: "right" },   // P. patronale
        },
        head: [["Libellé", "Base en DH", "Taux / nombre", "", "Gain", "Retenue", "P. patronale"]],
        body: salaryRows,
        foot: [["", "", "", "Totaux :", money(totalGains), money(totalRetenues), money(totalPatronales)]],
        footStyles: { fillColor: [240, 244, 252], textColor: 0, fontStyle: "bold" },
    });

    const y = doc.lastAutoTable.finalY + 8;
    doc.setFillColor(...PRIMARY);
    doc.rect(12, y, 142, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Net à payer", 15, y + 6);
    doc.text(numberToWordsFR(b.netAPayer), 42, y + 6);
    doc.setFillColor(...ACCENT);
    doc.rect(154, y, 44, 10, "F");
    doc.text(`${money(b.netAPayer)} MAD`, 176, y + 6, { align: "center" });
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.text(`Mode de paiement : ${b.modePaiement || "VIREMENT"}`, 12, y + 18);
    doc.setTextColor(...ACCENT);
    doc.setTextColor(0, 0, 0);
};

export default function BulletinsDePaiePage() {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const users = useSelector((state) => state.user?.users || []);
    const [selectedPeriodeId, setSelectedPeriodeId] = useState("");
    const [q, setQ] = useState("");
    const [matriculeDebut, setMatriculeDebut] = useState("");
    const [matriculeFin, setMatriculeFin] = useState("");
    const [selectedUserIds, setSelectedUserIds] = useState({});

    useEffect(() => {
        dispatch(getAllUsersRequest());
        ["periodes", "bulletins"].forEach((resource) => dispatch(payrollFetchResourceRequest(resource)));
    }, [dispatch]);

    useEffect(() => {
        if (!selectedPeriodeId && payroll.periodes?.length > 0) {
            setSelectedPeriodeId(String(payroll.periodes[0].id));
        }
    }, [payroll.periodes, selectedPeriodeId]);

    const activePeriod = useMemo(() => (payroll.periodes || []).find((p) => String(p.id) === String(selectedPeriodeId)), [payroll.periodes, selectedPeriodeId]);
    const bulletins = useMemo(() => (payroll.bulletins || []).filter((b) => !selectedPeriodeId || String(b.periodeId) === String(selectedPeriodeId)), [payroll.bulletins, selectedPeriodeId]);

    const filteredBulletins = useMemo(() => {
        return bulletins.filter((b) => {
            const user = users.find((u) => u.id === b.userId);
            const code = user?.employeeCode || b.employeeCode || "";
            const nameMatch = !q.trim() || userName(user).toLowerCase().includes(q.toLowerCase());
            const startOk = !matriculeDebut.trim() || code.toLowerCase() >= matriculeDebut.trim().toLowerCase();
            const endOk = !matriculeFin.trim() || code.toLowerCase() <= matriculeFin.trim().toLowerCase();
            return nameMatch && startOk && endOk;
        });
    }, [bulletins, users, q, matriculeDebut, matriculeFin]);

    const selectedBulletins = () => filteredBulletins.filter((b) => selectedUserIds[b.userId]);
    const token = () => sessionStorage.getItem("token") || localStorage.getItem("token");

    const loadDetails = async (targets) => {
        const detailed = [];
        for (const bulletin of targets) {
            const response = await getRequest({ url: `${API_BASE_URL}/api/lignes-bulletins-paie?bulletinId=${bulletin.id}`, token: token() });
            detailed.push({
                bulletin,
                user: users.find((u) => u.id === bulletin.userId),
                lines: response.data || [],
            });
        }
        return detailed;
    };

    const downloadPayslips = async (singleBulletin = null) => {
        const targets = singleBulletin ? [singleBulletin] : selectedBulletins();
        if (!targets.length) {
            Swal.fire({ icon: "warning", text: "Selectionnez au moins un bulletin." });
            return;
        }
        Swal.fire({ title: "Generation PDF...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        try {
            const detailed = await loadDetails(targets);
            const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            detailed.forEach((item, index) => {
                if (index > 0) doc.addPage();
                addPayslipPage(doc, item, monthLabel(activePeriod));
            });
            const fileName = singleBulletin
                ? `Bulletin_${safeFile(userName(detailed[0].user))}_${monthLabel(activePeriod)}.pdf`
                : `Bulletins_Paie_${monthLabel(activePeriod)}.pdf`;
            doc.save(fileName);
            Swal.close();
        } catch (error) {
            Swal.fire({ icon: "error", text: "Impossible de generer le PDF du bulletin." });
        }
    };

    const selectAll = () => {
        const shouldSelect = filteredBulletins.some((b) => !selectedUserIds[b.userId]);
        const next = {};
        if (shouldSelect) filteredBulletins.forEach((b) => { next[b.userId] = true; });
        setSelectedUserIds(next);
    };

    const btnPrimary = { background: "#204385", color: "#fff", border: "none" };
    const btnAccent  = { background: "#ee645f", color: "#fff", border: "none" };

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><FileText size={18} /> Bulletins de Paie</h2>
                    <p>Telechargement PDF des bulletins individuels de paie.</p>
                </div>
                <button className="pcyc-btn" style={btnPrimary} type="button" onClick={() => dispatch(payrollFetchResourceRequest("bulletins"))}>
                    <RefreshCcw size={15} /> Actualiser
                </button>
            </div>

            <div className="pcyc-toolbar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
                    <label style={{ minWidth: "200px" }}>
                        <span>Période</span>
                        <select value={selectedPeriodeId} onChange={(e) => setSelectedPeriodeId(e.target.value)}>
                            <option value="">Sélectionner</option>
                            {(payroll.periodes || []).map((p) => <option key={p.id} value={p.id}>{monthLabel(p)}</option>)}
                        </select>
                    </label>
                    <label style={{ width: "120px" }}><span>Matricule debut</span><input value={matriculeDebut} onChange={(e) => setMatriculeDebut(e.target.value)} /></label>
                    <label style={{ width: "120px" }}><span>Matricule fin</span><input value={matriculeFin} onChange={(e) => setMatriculeFin(e.target.value)} /></label>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                    <button className="pcyc-btn" style={btnAccent} type="button" onClick={() => downloadPayslips()}><Download size={15} /> PDF selection</button>
                </div>
            </div>

            <div className="pcyc-toolbar" style={{ borderBottom: "none", borderTopLeftRadius: "10px", borderTopRightRadius: "10px", margin: "14px 18px 0", background: "#f8fafc" }}>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    <button className="pcyc-btn" style={btnPrimary}><Users size={14} /> Bulletins ({filteredBulletins.length})</button>
                </div>
                <div className="pcyc-search" style={{ minWidth: "220px", marginLeft: "auto" }}>
                    <Search size={14} />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher collaborateur..." style={{ padding: "6px 10px 6px 30px" }} />
                </div>
            </div>

            <div className="pcyc-table-card" style={{ marginTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <table className="pcyc-table">
                    <thead>
                    <tr>
                        <th><input type="checkbox" checked={filteredBulletins.length > 0 && !filteredBulletins.some((b) => !selectedUserIds[b.userId])} onChange={selectAll} /></th>
                        <th>Matricule</th><th>Collaborateur</th><th>Brut</th><th>Retenues</th><th>Net à payer</th><th>Statut</th><th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredBulletins.length === 0
                        ? <tr><td colSpan="8" className="pcyc-empty">Aucun bulletin de paie disponible</td></tr>
                        : filteredBulletins.map((b) => {
                            const u = users.find((user) => user.id === b.userId);
                            return (
                                <tr key={b.id}>
                                    <td><input type="checkbox" checked={!!selectedUserIds[b.userId]} onChange={() => setSelectedUserIds((prev) => ({ ...prev, [b.userId]: !prev[b.userId] }))} /></td>
                                    <td><span className="pcyc-code">{u?.employeeCode || b.employeeCode || "N/A"}</span></td>
                                    <td><strong>{userName(u)}</strong></td>
                                    <td>{money(b.salaireBrutImposable)} MAD</td>
                                    <td>{money(b.totalRetenues)} MAD</td>
                                    <td><strong>{money(b.netAPayer)} MAD</strong></td>
                                    <td><span className={`pcyc-status ${b.statut}`}>{b.statut}</span></td>
                                    <td className="pcyc-row-actions">
                                        <button type="button" onClick={() => downloadPayslips(b)} title="Telecharger PDF"><Download size={14} /> PDF</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
